import { rem } from 'polished'
import React from 'react'
import { EXPERT_FEATURES } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import {
  Button,
  BUTTON_INTENT,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from 'components/Button'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import {
  PRODUCT_AVAILABILITY,
  type ProductAvailability,
} from 'constants/common'
import { environment } from 'constants/environment'
import { useShouldRenderExpertContent } from 'hooks'
import { GA4_EVENTS, useGoogleAnalytics } from 'providers/googleAnalytics'
import { COLOR_INTENT, COLOR_NAMES, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { ShowroomButton } from './ShowroomButton'

const BUTTON_WIDTH = { MOBILE: '100%', TABLET: rem(300) }

const BUTTON_TEXT = {
  ADD_TO_CART: 'Add to cart',
  CONTACT_US: 'Contact us',
  ENQUIRE: 'Inquire',
  NOT_IN_STOCK: 'No more items left in stock',
  SOLD_OUT: 'Sold Out',
}

const BUTTON_TEXT_MAP = {
  [PRODUCT_AVAILABILITY.IN_STOCK]: BUTTON_TEXT.ADD_TO_CART,
  [PRODUCT_AVAILABILITY.MADE_TO_ORDER]: BUTTON_TEXT.ADD_TO_CART,
  [PRODUCT_AVAILABILITY.ENQUIRE]: BUTTON_TEXT.ENQUIRE,
  [PRODUCT_AVAILABILITY.SOLD_OUT]: BUTTON_TEXT.SOLD_OUT,
} as const

const StyledButton = styled(Button)`
  ${({ isDisabled }) => `
    ${
      isDisabled &&
      `
      background-color: ${COLOR_INTENT.GRAY_3};
      border: none;
      color: ${COLOR_INTENT.TEXT_ON_DISABLED};
      opacity: 1;
      `
    }`}
`

interface AddToCartButtonProps {
  productVariantId: number
  productId: number
  onAddToCart: (event?: React.FormEvent<HTMLFormElement>) => void
  isAddToCartLoading: boolean
  isAddToCartDisabled: boolean
  isPriceUponRequest: boolean
  isSoldOut?: boolean
  isVariantPublished: boolean
  availability: ProductAvailability
  width?: React.ComponentProps<typeof Grid>['width']
}

interface RenderButtonProps {
  onClick?: (event?: React.FormEvent<HTMLFormElement>) => void
  isLoading: boolean
  isDisabled: boolean
  intent: (typeof BUTTON_INTENT)[keyof typeof BUTTON_INTENT]
  'data-test-id': (typeof DATA_TEST_ID)[keyof typeof DATA_TEST_ID]
}

const RenderButton: React.FC<React.PWC<RenderButtonProps>> = ({
  onClick = undefined,
  isLoading,
  isDisabled,
  intent,
  children,
  ...props
}) => (
  <StyledButton
    onClick={onClick}
    isLoading={isLoading}
    isDisabled={isDisabled}
    disabled={isDisabled}
    intent={intent}
    size={BUTTON_SIZE.L}
    {...props}
  >
    <Flex justifyContent="center" alignItems="center">
      {children}
    </Flex>
  </StyledButton>
)

const RenderButtonText = ({ icon: IconComponent, color, text, isDisabled }) => (
  <>
    {!isDisabled && IconComponent && (
      <IconComponent color={color} size={ICON_SIZE.PX_16} />
    )}
    <Text
      ml={SPACE.PX_10}
      fontWeight={FONT_WEIGHT.MEDIUM}
      fontSize={FONT_SIZE.PX_16}
    >
      {text}
    </Text>
  </>
)

export const AddToCartButton: React.FC<React.PWC<AddToCartButtonProps>> = ({
  productVariantId,
  productId,
  onAddToCart,
  isAddToCartLoading,
  isAddToCartDisabled,
  isVariantPublished,
  isPriceUponRequest,
  isSoldOut,
  availability,
  width = BUTTON_WIDTH,
}) => {
  const { shouldRenderExpertContent } = useShouldRenderExpertContent()
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const isSoldOutOrNotInStock = isSoldOut || isAddToCartDisabled
  let buttonText = BUTTON_TEXT_MAP[availability] ?? BUTTON_TEXT.ADD_TO_CART

  if (isSoldOutOrNotInStock) {
    buttonText = isSoldOut ? BUTTON_TEXT.SOLD_OUT : BUTTON_TEXT.NOT_IN_STOCK
  }

  const isDisabled =
    availability === PRODUCT_AVAILABILITY.SOLD_OUT ||
    isAddToCartDisabled ||
    !isVariantPublished ||
    isSoldOut

  const handleClickEnquire = () => {
    if (environment.CUSTOMER_SERVICE_FRAMEWORK === 'intercom') {
      window?.Intercom?.('show')
    } else {
      window?.FrontChat?.('show')
    }
  }

  return (
    <Grid gridGap={SPACE.PX_14} width={width}>
      {!isPriceUponRequest &&
        availability !== PRODUCT_AVAILABILITY.ENQUIRE &&
        isVariantPublished && (
          <RenderButton
            onClick={onAddToCart}
            isLoading={isAddToCartLoading}
            isDisabled={isDisabled}
            intent={BUTTON_INTENT.PRIMARY}
            data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_ADD_TO_CART_BTN}
          >
            <RenderButtonText
              icon={Icon.Plus}
              color={COLOR_NAMES.WHITE}
              text={buttonText}
              isDisabled={isDisabled}
            />
          </RenderButton>
        )}

      {(isPriceUponRequest ||
        availability === PRODUCT_AVAILABILITY.ENQUIRE) && (
        <Button
          onClick={handleClickEnquire}
          width="100%"
          intent={BUTTON_INTENT.PRIMARY}
          isLoading={isAddToCartLoading}
          isDisabled={false}
          data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_INQUIRE_BTN}
          variant={BUTTON_VARIANT.OUTLINE}
          aria-label="inquire"
        >
          <RenderButtonText
            icon={Icon.Send}
            color={COLOR_NAMES.WHITE}
            text={BUTTON_TEXT.ENQUIRE}
            isDisabled={false}
          />
        </Button>
      )}
      {shouldRenderExpertContent(
        EXPERT_FEATURES.PRODUCT_DETAIL_PAGE.ADD_TO_SHOWROOM
      ) && (
        <ShowroomButton
          productVariantId={productVariantId}
          productId={productId}
        />
      )}
      {availability !== PRODUCT_AVAILABILITY.ENQUIRE && !isPriceUponRequest && (
        <Button
          width="100%"
          variant={
            isVariantPublished ? BUTTON_VARIANT.OUTLINE : BUTTON_VARIANT.DEFAULT
          }
          size={BUTTON_SIZE.L}
          onClick={() => {
            handleClickEnquire()
            executeDataToDataLayer({
              event: GA4_EVENTS.CLICK,
              overrideData: {
                text: BUTTON_TEXT.CONTACT_US,
              },
            })
          }}
          aria-label="Contact us"
        >
          <Flex justifyContent="center" alignItems="center">
            <Icon.Send size={ICON_SIZE.PX_16} />
            <Text ml={SPACE.PX_10} fontWeight={FONT_WEIGHT.MEDIUM}>
              {BUTTON_TEXT.CONTACT_US}
            </Text>
          </Flex>
        </Button>
      )}
    </Grid>
  )
}
